import { useEffect, useState } from "react";
import {
    ResponsiveContainer,
    ComposedChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    Bar,
    Line,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp } from "lucide-react";

/**
 * Weekly cadence chart — clicks → signups → conversions for the current
 * affiliate/referrer over the last N weeks. Powered by the
 * /api/member/referrals/analytics endpoint.
 */
const COLORS = {
    clicks: "#8FA5C6",     // navy-200
    signups: "#D4AF37",    // gold-500
    conversions: "#001c50", // navy-700
};

function shortWeekLabel(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function StatPill({ label, value, color }) {
    return (
        <div className="rounded-xl bg-cream border border-navy-100 px-3 py-2">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-navy-500">
                <span
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ background: color }}
                />
                {label}
            </div>
            <div className="text-navy-700 font-display text-2xl mt-0.5">{value}</div>
        </div>
    );
}

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-xl bg-navy-700 text-cream text-xs px-3 py-2 shadow-lg">
            <div className="font-semibold mb-1">Week of {shortWeekLabel(label)}</div>
            {payload.map((p) => (
                <div key={p.dataKey} className="flex items-center gap-2">
                    <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ background: p.color }}
                    />
                    <span className="opacity-70 capitalize">{p.dataKey}:</span>
                    <span className="font-mono">{p.value}</span>
                </div>
            ))}
        </div>
    );
}

export default function WeeklyCadenceChart() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/member/referrals/analytics?weeks=8")
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    const chartData =
        data?.weeks?.map((w) => ({
            ...w,
            label: shortWeekLabel(w.week_start),
        })) || [];

    return (
        <div
            data-testid="affiliate-analytics-chart"
            className="rounded-3xl border border-navy-100 bg-white p-6 lg:p-8"
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2">
                        <TrendingUp size={18} className="text-gold-600" />
                        <h2 className="font-display text-2xl text-navy-700">
                            Last 8 weeks
                        </h2>
                    </div>
                    <p className="text-sm text-navy-500 mt-1">
                        Clicks on your referral link, signups it produced, and paid
                        Perx memberships that followed. See which content is landing.
                    </p>
                </div>
                {data && (
                    <div className="flex gap-2">
                        <StatPill
                            label="Clicks"
                            value={data.total.clicks}
                            color={COLORS.clicks}
                        />
                        <StatPill
                            label="Signups"
                            value={data.total.signups}
                            color={COLORS.signups}
                        />
                        <StatPill
                            label="Conversions"
                            value={data.total.conversions}
                            color={COLORS.conversions}
                        />
                    </div>
                )}
            </div>

            <div className="mt-6 h-[300px]">
                {loading && (
                    <div className="h-full flex items-center justify-center text-navy-400 text-sm">
                        Loading analytics…
                    </div>
                )}
                {!loading && chartData.length > 0 && (
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                            data={chartData}
                            margin={{ top: 10, right: 20, bottom: 10, left: 0 }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#EDE7D4"
                                vertical={false}
                            />
                            <XAxis
                                dataKey="week_start"
                                tickFormatter={shortWeekLabel}
                                stroke="#5a6a86"
                                fontSize={11}
                                tickLine={false}
                            />
                            <YAxis
                                stroke="#5a6a86"
                                fontSize={11}
                                tickLine={false}
                                allowDecimals={false}
                                width={30}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend
                                wrapperStyle={{ fontSize: 11, color: "#5a6a86" }}
                                iconType="circle"
                            />
                            <Bar
                                dataKey="clicks"
                                fill={COLORS.clicks}
                                radius={[4, 4, 0, 0]}
                                barSize={14}
                            />
                            <Bar
                                dataKey="signups"
                                fill={COLORS.signups}
                                radius={[4, 4, 0, 0]}
                                barSize={14}
                            />
                            <Line
                                type="monotone"
                                dataKey="conversions"
                                stroke={COLORS.conversions}
                                strokeWidth={2.5}
                                dot={{ r: 4, fill: COLORS.conversions }}
                                activeDot={{ r: 6 }}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                )}
                {!loading && chartData.every((w) => !w.clicks && !w.signups && !w.conversions) && (
                    <div className="h-full flex flex-col items-center justify-center text-center text-navy-500 text-sm">
                        <TrendingUp size={28} className="text-navy-300 mb-2" />
                        <div className="font-semibold text-navy-700">
                            Your first campaign starts here.
                        </div>
                        <div className="text-xs text-navy-400 mt-1 max-w-xs">
                            Share your referral link or print the QR card and you&apos;ll
                            see clicks, signups and paid conversions land here week by
                            week.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
